import {
  GetQuestionReviewTaskByIdDocument,
  OrderType,
  useSearchUsersByAppRoleFiltersLazyQuery,
  useUpdateAndCreateQuestionReviewSubtasksMutation,
  WorkflowCode,
} from '@exam-management/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Form,
  FormButton,
  FormDateInput,
  FormSearchSelection,
  FormTextarea,
  PersonOutlinedIcon,
  useTheme,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useQuestionReviewTaskDetails } from '../../providers';

import { AddReviewerFormSchema, type AddReviewerFormValues } from './schema';

interface AddReviewerModalProps {
  open: boolean;
  onClose: () => void;
}

export default function AddReviewerModal(props: Readonly<AddReviewerModalProps>) {
  const { open, onClose } = props;

  const theme = useTheme();
  const { showPromiseSnackbar } = useSnackbar();

  const { taskDetails } = useQuestionReviewTaskDetails();

  const formContext = useForm<AddReviewerFormValues>({
    resolver: zodResolver(AddReviewerFormSchema),
  });

  const [searchUser, { data: userListData, loading: isSearchingUserList }] =
    useSearchUsersByAppRoleFiltersLazyQuery();

  const handleSearchUser = (inputValue: string | null) => {
    const [firstname, lastname] = inputValue?.trim().split(' ') ?? [];
    searchUser({
      variables: {
        appRoleNames: [],
        order: {
          type: OrderType.Asc,
          field: 'firstname',
        },
        paging: {
          currentPage: 0,
          pageSize: 100,
        },
        search: {
          firstname: firstname,
          lastname: lastname,
          email: '',
          status: '',
        },
      },
      fetchPolicy: 'network-only',
    });
  };

  const userOptions = useMemo(() => {
    if (isSearchingUserList || !userListData) return [];

    const { users } = userListData.UsBoSearchUsersWithAppRoleFilters;

    const excludedUserIds = [
      taskDetails.ownerId,
      ...taskDetails.subtasks.map((subtask) => subtask.assigneeId),
    ];

    return users
      .filter((user) => !excludedUserIds.includes(user.id))
      .map((user) => ({
        label: `${user.firstname} ${user.lastname}`,
        value: user.id,
      }));
  }, [isSearchingUserList, userListData, taskDetails.subtasks, taskDetails.ownerId]);

  const [addReviewer, { loading: isAddingReviewer }] =
    useUpdateAndCreateQuestionReviewSubtasksMutation({
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionReviewTaskByIdDocument,
          variables: {
            input: {
              taskId: taskDetails.id,
            },
          },
        },
      ],
    });

  const handleAddReviewer = async (data: AddReviewerFormValues) => {
    await addReviewer({
      variables: {
        input: {
          mainTaskId: taskDetails.id,
          workflowCode: WorkflowCode.QuestionReview,
          subtasks: [
            {
              assigneeId: data.reviewer.value,
              dueDate: data.dueDate,
              description: data.description,
            },
          ],
        },
      },
    });
    onClose();
  };

  const handleResetForm = () => {
    formContext.reset();
  };

  const isFormSubmitting = formContext.formState.isSubmitting || isAddingReviewer;
  const handleClose = () => {
    if (isFormSubmitting) {
      return;
    }

    handleResetForm();
    onClose();
  };

  const handleSubmit = (data: AddReviewerFormValues) => {
    showPromiseSnackbar({
      message: {
        success: 'Reviewer has been added',
        error: 'Failed to add reviewer',
        pending: 'Adding reviewer...',
      },
      promise: () => handleAddReviewer(data),
    });
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown={isFormSubmitting}
        maxWidth='md'
        slotProps={{
          paper: { component: Form, onSubmit: formContext.handleSubmit(handleSubmit) },
          transition: {
            onExited: handleResetForm,
          },
        }}
      >
        <DialogTitle onClose={handleClose}>Add Reviewer</DialogTitle>
        <DialogContent
          dividers
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            p: theme.spacing(6),
            gap: theme.spacing(4),
          })}
        >
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <FormSearchSelection
              name='reviewer'
              label='Reviewer'
              mode='fetch-debounce'
              options={userOptions}
              placeholder='กรุณาเลือก Reviewer'
              loading={isSearchingUserList}
              required
              onSearch={handleSearchUser}
              sx={{ flex: 1 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <PersonOutlinedIcon
                      sx={(t) => ({
                        fontSize: '20px',
                        mr: t.spacing(2),
                        color: t.palette.grey[500],
                      })}
                    />
                  ),
                },
              }}
            />
            <Box sx={{ flex: 1 }}>
              <FormDateInput
                name='dueDate'
                label='Due Date'
                inputFormat='DD-MM-YYYY'
                placeholder='กรุณาเลือก Due Date'
                minDate={new Date()}
                zIndex={theme.zIndex.modal + 1}
                withinPortal
                required
              />
            </Box>
          </Flex>
          <FormTextarea
            name='description'
            label='รายละเอียดเพิ่มเติม'
            placeholder='กรอกรายละเอียดเพิ่มเติม'
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            size='large'
            onClick={handleClose}
            disabled={isFormSubmitting}
          >
            Cancel
          </Button>
          <FormButton size='large' label='Add Reviewer' disabled={isFormSubmitting} />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
